import React, { useCallback, useMemo } from 'react'
import { Users, Maximize2, Check } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { useWardStore } from '@/stores/wardStore'
import { useComparisonStore } from '@/stores/comparisonStore'
import { VI_MESSAGES } from '@/constants/vietnamese'
import { formatNumber } from '@/utils/vietnamese-format'
import type { WardInfo } from '@/types/ward'
import { cn } from '@/lib/utils'

interface WardCardProps {
  ward: WardInfo
}

function WardCardComponent({ ward }: WardCardProps) {
  const { selectedWard, selectWard, hoverWard } = useWardStore()
  const { isComparing, addWard, removeWard, isWardSelected, canAddMore } = useComparisonStore()
  const isSelected = selectedWard?.objectid === ward.objectid
  const isInComparison = isWardSelected(ward.objectid)
  const [isAnimating, setIsAnimating] = React.useState(false)

  const handleClick = useCallback(() => {
    if (isComparing) {
      // In comparison mode, add/remove from comparison
      if (isInComparison) {
        removeWard(ward.objectid)
      } else if (canAddMore()) {
        addWard(ward)
      }
    } else {
      // Normal selection mode
      if (!isSelected) {
        setIsAnimating(true)
        setTimeout(() => setIsAnimating(false), 600)
      }
      selectWard(isSelected ? null : ward)
    }
  }, [isComparing, isInComparison, removeWard, ward, canAddMore, addWard, isSelected, selectWard])

  const handleMouseEnter = useCallback(() => {
    hoverWard(ward.objectid)
  }, [hoverWard, ward.objectid])

  const handleMouseLeave = useCallback(() => {
    hoverWard(null)
  }, [hoverWard])

  // Memoize className calculations
  const cardClassName = useMemo(
    () =>
      cn(
        'ward-card-transition relative mx-4 cursor-pointer border-gray-200',
        isSelected && !isComparing && 'ward-selected shadow-md ring-2 ring-[#ff6b35]',
        isInComparison && 'bg-blue-50 shadow-md ring-2 ring-blue-500',
        isAnimating && 'ward-select-animation',
        isComparing && 'multi-select-mode'
      ),
    [isSelected, isComparing, isInComparison, isAnimating]
  )

  const checkboxClassName = useMemo(
    () =>
      cn(
        'absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-md border-2 transition-all',
        isInComparison ? 'border-blue-500 bg-blue-500' : 'border-gray-300 bg-white'
      ),
    [isInComparison]
  )

  return (
    <Card
      className={cardClassName}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="px-3">
        {/* Comparison checkbox */}
        {isComparing && (
          <div className={checkboxClassName}>
            {isInComparison && <Check className="h-4 w-4 text-white" />}
          </div>
        )}

        {/* Tên phường và quận */}
        <div className="mb-2">
          <h3 className="truncate text-lg font-semibold" title={ward.xaphuong}>
            {ward.xaphuong}
          </h3>
          {/* {ward.quanhuyen && ( */}
          {/*   <p className="text-sm text-gray-500">{ward.quanhuyen}</p> */}
          {/* )} */}
        </div>

        {/* Thông tin thống kê */}
        <div className="grid grid-cols-2 gap-3">
          {/* Dân số */}
          {ward.danso && (
            <div className="flex items-start gap-2">
              <Users className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">{VI_MESSAGES.WARD.LABELS.POPULATION}</p>
                <p className="text-sm font-semibold text-gray-900">{formatNumber(ward.danso)}</p>
              </div>
            </div>
          )}

          {/* Diện tích */}
          {ward.dientichpl && (
            <div className="flex items-start gap-2">
              <Maximize2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">{VI_MESSAGES.WARD.LABELS.AREA}</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatNumber(ward.dientichpl)} km²
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Chủ tịch phường */}
        {ward.chutich && (
          <div className="border-t border-gray-100 pt-2">
            <p className="text-xs text-gray-500">{VI_MESSAGES.WARD.LABELS.CHAIRMAN}</p>
            <p className="truncate text-sm font-medium text-gray-900" title={ward.chutich}>
              {ward.chutich}
            </p>
          </div>
        )}
      </div>
    </Card>
  )
}

// Memoize the component to prevent unnecessary re-renders
export const WardCard = React.memo(WardCardComponent, (prevProps, nextProps) => {
  // Re-render only if the ward data changes
  return prevProps.ward.objectid === nextProps.ward.objectid
})
